/**
 * Minimal, generation-fenced browser resume state.
 *
 * The server owns gameplay. This module stores only enough opaque seat context
 * to ask for a fresh private projection; it never persists cards or hidden
 * state and never treats the local hint as proof that a seat still exists.
 */
import { ActionIntent } from './ui/action-intent.js';
import { CardDrag } from './ui/card-drag.js';
import { SoundEffects } from './ui/sound-effects.js';
import { SpellAnnounce } from './ui/spell-announce.js';
import { TurnBanner } from './ui/turn-banner.js';

const GameResume = (function () {
    'use strict';

    const STORAGE_KEY = 'moonfall:game-resume:v1';
    const VERSION = 1;
    const MAX_HINT_AGE_MS = 15 * 60_000;
    let generation = 0;
    let attemptSequence = 0;
    let activeAttempt = null;
    let suspended = false;

    function normalizeId(value) {
        if (typeof value !== 'string' || !value || value.length > 128) return null;
        return value === value.trim() ? value : null;
    }

    function normalizeRevision(value) {
        const revision = Number(value);
        return Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
    }

    function normalizeStoredHint(value) {
        if (!value || value.version !== VERSION) return null;
        const roomId = normalizeId(value.roomId);
        const matchId = normalizeId(value.matchId);
        const playerId = normalizeId(value.playerId);
        const savedAt = Number(value.savedAt);
        const now = Date.now();
        if (
            !roomId
            || !matchId
            || !playerId
            || (value.mode !== 'authenticated' && value.mode !== 'guest')
            || !Number.isFinite(savedAt)
            || savedAt > now + 60_000
            || now - savedAt > MAX_HINT_AGE_MS
        ) return null;
        return {
            version: VERSION,
            roomId,
            matchId,
            playerId,
            mode: value.mode,
            isTutorial: value.isTutorial === true,
            resumeRevision: normalizeRevision(value.resumeRevision),
        };
    }

    function removeStoredHint() {
        try { window.sessionStorage?.removeItem(STORAGE_KEY); } catch (_) { }
    }

    function read() {
        try {
            const raw = window.sessionStorage?.getItem(STORAGE_KEY);
            if (!raw) return null;
            const hint = normalizeStoredHint(JSON.parse(raw));
            if (hint) return hint;
        } catch (_) { }
        removeStoredHint();
        return null;
    }

    function remember(gameState, options = {}) {
        const roomId = normalizeId(gameState?.roomId);
        const matchId = normalizeId(gameState?.matchId);
        const playerId = normalizeId(gameState?.player?.id || gameState?.playerId);
        if (!roomId || !matchId || !playerId) return false;
        const hint = {
            version: VERSION,
            roomId,
            matchId,
            playerId,
            mode: options.isGuest === true ? 'guest' : 'authenticated',
            isTutorial: options.isTutorial === true,
            resumeRevision: normalizeRevision(
                options.resumeRevision ?? gameState?.resumeRevision,
            ),
            savedAt: Date.now(),
        };
        const replacesActiveAttempt = activeAttempt
            && !sameScope(activeAttempt.hint, hint);
        try {
            window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(hint));
            if (replacesActiveAttempt) invalidateAttempts();
            return true;
        } catch (_) {
            return false;
        }
    }

    function invalidateAttempts() {
        generation += 1;
        activeAttempt = null;
        return generation;
    }

    function clear() {
        removeStoredHint();
        invalidateAttempts();
    }

    function clearIfScope(scope) {
        if (!sameScope(read(), scope)) return false;
        clear();
        return true;
    }

    function cancelAttempt() {
        return invalidateAttempts();
    }

    function cancelTransientPresentation() {
        try { SoundEffects?.stopAll?.(); } catch (_) { }
        try { CardDrag?.reset?.(); } catch (_) { }
        // The action-intent dialog is deliberately NOT dismissed here. It is a
        // live authoritative decision, not presentation: the server holds a
        // pending request keyed by a requestId that exists nowhere else on the
        // client, keeps `actionInProgress` set until that request is committed
        // or cancelled, and refuses End Turn while it is set. Clearing it on
        // `document.hidden` deadlocked the match — switch tabs mid-choice and
        // the player came back unable to act at all. A dialog that is still
        // pending must survive backgrounding; one that is not pending was
        // already torn down by its own completion path.
        try {
            if (ActionIntent?.hasPendingRequest?.() !== true) {
                ActionIntent?.clear?.('document-hidden');
            }
        } catch (_) { }
        try { SpellAnnounce?.dismiss?.(); } catch (_) { }
        try { TurnBanner?.reset?.(); } catch (_) { }
    }

    function suspend() {
        if (suspended) return generation;
        suspended = true;
        invalidateAttempts();
        document.body?.classList?.add('game-suspended');
        cancelTransientPresentation();
        return generation;
    }

    function wake() {
        suspended = false;
        invalidateAttempts();
        document.body?.classList?.remove('game-suspended');
        return generation;
    }

    function sameScope(left, right) {
        return Boolean(
            left
            && right
            && left.roomId === right.roomId
            && left.matchId === right.matchId
            && left.playerId === right.playerId
        );
    }

    function beginAttempt() {
        if (suspended || activeAttempt) return null;
        const hint = read();
        if (!hint) return null;
        activeAttempt = Object.freeze({
            id: ++attemptSequence,
            generation,
            hint,
        });
        return activeAttempt;
    }

    function isCurrent(attempt) {
        if (
            !attempt
            || attempt !== activeAttempt
            || attempt.generation !== generation
            || suspended
        ) return false;
        return sameScope(attempt.hint, read());
    }

    function finishAttempt(attempt) {
        if (!isCurrent(attempt)) return false;
        activeAttempt = null;
        return true;
    }

    return {
        STORAGE_KEY,
        beginAttempt,
        cancelAttempt,
        clear,
        clearIfScope,
        finishAttempt,
        isCurrent,
        read,
        remember,
        suspend,
        wake,
    };
})();

export { GameResume };
